import { Css, useComputed, useTestIds } from "@homebound/beam";
import { Fragment, type ReactNode } from "react";
import { AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { type ObjectState } from "src/utils/formState";
import { type AiFieldProposalFormValue } from "./utils/aiFieldProposalForm";
import { isAiProposedDeletion } from "./utils/bindAiProposal";

type AiProposalValueProps = {
  aiProposal: ObjectState<AiFieldProposalFormValue> | undefined;
  originalValue: ReactNode;
  children: ReactNode;
};

/** Shows the saved value as removed instead of an editable field for entity DELETE proposals. */
export function AiProposalValue(props: AiProposalValueProps) {
  const { aiProposal, originalValue, children } = props;
  const tid = useTestIds(props, "removedValue");
  const isDeletion = useComputed(() => isAiProposedDeletion(aiProposal), [aiProposal]);
  // Beam's public fields overwrite originalValue when proposedValue is absent. Keep the static
  // removal treatment here until Beam supports original-only proposals through those public APIs.
  return isDeletion ? (
    <span {...tid} css={Css.tdlt.gray700.$}>
      {originalValue}
    </span>
  ) : (
    <>{children}</>
  );
}

/** A pending proposal's cited pages, for a field's `helperText` slot. */
export type AiFieldSourcesProps = { sourceUrl: string; pageNumbers: readonly number[] };

/** Once a proposal is accepted or rejected we stop showing its sources, matching the field's AI highlight clearing. */
export function pendingSources(
  afpState: ObjectState<AiFieldProposalFormValue> | undefined,
): AiFieldSourcesProps | undefined {
  if (afpState?.status.value !== AiFieldProposalStatus.Pending) return undefined;
  const { sourceUrl, pageNumbers } = afpState;
  if (!sourceUrl.value || !pageNumbers.value?.length) return undefined;
  return { sourceUrl: sourceUrl.value, pageNumbers: pageNumbers.value };
}

export function AiFieldSources(props: AiFieldSourcesProps) {
  const { sourceUrl, pageNumbers } = props;
  const tid = useTestIds(props, "aiFieldSources");
  return (
    <span {...tid}>
      Sources: {/* TODO: Plan-1846, open new source pdf viewer page */}
      {pageNumbers.map((page, i) => (
        <Fragment key={page}>
          {i > 0 && ", "}
          {/* Browsers' PDF viewers honor the `#page=N` fragment. */}
          <a
            href={`${sourceUrl}#page=${page}`}
            target="_blank"
            rel="noreferrer"
            css={Css.xsSb.blue600.tdn.$}
            {...tid.page}
          >
            Page {page}
          </a>
        </Fragment>
      ))}
    </span>
  );
}
